import React, { useCallback, useState } from 'react';
import { 
  Drawer, 
  Box, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  useMediaQuery, 
  useTheme,
  IconButton,
  Collapse
} from '@mui/material';
import alien_realty_logo from '../../assets/images/5.png';
import { 
  Menu as MenuIcon, 
  ChevronLeft, 
  ChevronRight,
  People,
  ExpandLess,
  ExpandMore,
  ContactPhone,
  Assignment,
  Dashboard
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router/dist';

interface CustomDrawerProps {
  children: React.ReactNode;
}

export const CustomDrawer = ({ children }: CustomDrawerProps) => {
  const theme = useTheme();
  const { pageUrl } = useParams()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  
  const [drawerWidth, setDrawerWidth] = useState(240);
  const [isResizing, setIsResizing] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [crmOpen, setCrmOpen] = useState(false);
  
  const minWidth = 180;
  const maxWidth = 500;
  const collapsedWidth = 65;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCollapseToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleCrmClick = () => {
    setCrmOpen(!crmOpen);
  };

  const handleMouseDown = useCallback(() => {
    if (!isMobile && !isCollapsed) {
      setIsResizing(true);
    }
  }, [isMobile, isCollapsed]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing || isMobile) return;
    
    const newWidth = e.clientX;
    if (newWidth >= minWidth && newWidth <= maxWidth) {
      setDrawerWidth(newWidth);
    }
  }, [isResizing, isMobile]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const menuItems = [
    { text: 'Muro', icon: <Dashboard sx={{ fontSize: 20 }} />, path: pageUrl ? `/company/${pageUrl}/properties` : '/' },
    { 
      text: 'CRM', 
      icon: <People sx={{ fontSize: 20 }} />, 
      path: '/crm',
      hasSubmenu: true,
      subItems: [
        { text: 'Solicitudes', icon: <Assignment sx={{ fontSize: 18 }} />, path: pageUrl ? `/company/${pageUrl}/crm/requests` : '/crm/requests' },
        { text: 'Contactos', icon: <ContactPhone sx={{ fontSize: 18 }} />, path: pageUrl ? `/company/${pageUrl}/crm/contacts` : '/crm/contacts' },
      ]
    },

  ];

  const drawerContent = (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%', 
      position: 'relative' 
    }}>
      {/* Logo Section */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        p: 2,
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        {!isCollapsed && (
          <img 
            src={alien_realty_logo} 
            alt="Logo" 
            style={{ maxWidth: '100%', height: 'auto' }} 
          />
        )}
        {isCollapsed && (
          <Box sx={{ 
            width: 40, 
            height: 40, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <MenuIcon sx={{ fontSize: 28 }} />
          </Box>
        )}
      </Box>

      {/* Menu Items */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List>
          {menuItems.map((item) => (
            <React.Fragment key={item.text}>
              <ListItem disablePadding>
                <ListItemButton 
                  onClick={() => {
                    if (item.hasSubmenu) {
                      handleCrmClick();
                    } else {
                      navigate(item.path);
                      if (isMobile) handleDrawerToggle();
                    }
                  }}
                  sx={{ py: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  {!isCollapsed && (
                    <>
                      <ListItemText primary={item.text} />
                      {item.hasSubmenu && (
                        crmOpen ? <ExpandLess /> : <ExpandMore />
                      )}
                    </>
                  )}
                </ListItemButton>
              </ListItem>
              
              {/* Submenú */}
              {item.hasSubmenu && item.subItems && !isCollapsed && (
                <Collapse in={crmOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem) => (
                      <ListItem key={subItem.text} disablePadding>
                        <ListItemButton 
                          sx={{ pl: 4, py: 1 }}
                          onClick={() => {
                            navigate(subItem.path);
                            if (isMobile) handleDrawerToggle();
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 40 }}>
                            {subItem.icon}
                          </ListItemIcon>
                          <ListItemText primary={subItem.text} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>

      {/* Collapse Button - Desktop only */}
      {!isMobile && (
        <>
          <Box sx={{ 
            borderTop: `1px solid ${theme.palette.divider}`,
            p: 1,
            display: 'flex',
            justifyContent: 'center'
          }}>
            <IconButton onClick={handleCollapseToggle} size="small">
              {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
            </IconButton>
          </Box>

          {/* Resize Handle */}
          {!isCollapsed && (
            <Box
              onMouseDown={handleMouseDown}
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: '5px',
                cursor: 'col-resize',
                bgcolor: 'transparent',
                '&:hover': {
                  bgcolor: 'primary.main',
                  opacity: 0.5,
                },
                transition: 'background-color 0.2s',
                zIndex: 1,
              }}
            />
          )}
        </>
      )}
    </Box>
  );

  const currentDrawerWidth = isMobile ? 240 : (isCollapsed ? collapsedWidth : drawerWidth);

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: 240,
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: currentDrawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: currentDrawerWidth,
            boxSizing: 'border-box',
            transition: isResizing ? 'none' : 'width 0.3s ease',
            position: 'relative',
            overflowX: 'hidden',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {children}
    </Box>
  );
}